/**
 * Klient HTTP z automatycznym odnawianiem sesji.
 *
 * Gdy backend odpowie 401, klient jednorazowo próbuje odświeżyć token
 * i powtarza żądanie. Równoległe żądania czekają na to samo odświeżenie —
 * inaczej każde z nich zużyłoby token odświeżający i serwer uznałby to za
 * kradzież sesji (rotacja tokenów wykrywa ponowne użycie).
 */

import type { ApiErrorBody, TokenPair } from './types'
import { tokenStorage } from './tokenStorage'

const API_BASE = import.meta.env.VITE_API_URL ?? '/api/v1'

/** Błąd HTTP z czytelnym komunikatem po polsku i błędami pól formularza. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly fieldErrors: Record<string, string>

  constructor(status: number, body: Partial<ApiErrorBody>) {
    super(body.message ?? 'Wystąpił nieoczekiwany błąd.')
    this.name = 'ApiError'
    this.status = status
    this.code = body.code ?? 'unknown_error'
    this.fieldErrors = Object.fromEntries(
      (body.details ?? []).map((detail) => [detail.field, detail.message]),
    )
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  get isConflict(): boolean {
    return this.status === 409
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  /** Pomija próbę odświeżenia sesji — używane przez same endpointy auth. */
  skipAuthRefresh?: boolean
  signal?: AbortSignal
}

/** Trwające odświeżanie — współdzielone przez wszystkie równoległe żądania. */
let refreshInFlight: Promise<string | null> | null = null

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path}`
  if (!query) return url

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value))
    }
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as Partial<ApiErrorBody>
    return new ApiError(response.status, body)
  } catch {
    return new ApiError(response.status, {
      message:
        response.status >= 500
          ? 'Serwer nie odpowiada. Spróbuj ponownie za chwilę.'
          : 'Wystąpił nieoczekiwany błąd.',
    })
  }
}

/** Odświeża token dostępowy. Zwraca nowy token albo `null`, jeśli sesja padła. */
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = tokenStorage.getRefreshToken()
  if (!refreshToken) return null

  try {
    const response = await fetch(buildUrl('/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
    if (!response.ok) return null

    const tokens = (await response.json()) as TokenPair
    tokenStorage.save(tokens)
    return tokens.access_token
  } catch {
    return null
  }
}

function ensureRefresh(): Promise<string | null> {
  refreshInFlight ??= refreshAccessToken().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {}
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }
  const accessToken = tokenStorage.getAccessToken()
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`
  }

  return fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response: Response
  try {
    response = await send(path, options)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError(0, {
      code: 'network_error',
      message: 'Brak połączenia z serwerem. Sprawdź sieć i spróbuj ponownie.',
    })
  }

  if (response.status === 401 && !options.skipAuthRefresh) {
    const newToken = await ensureRefresh()
    if (newToken) {
      response = await send(path, options)
    } else {
      tokenStorage.notifySessionExpired()
      throw await parseError(response)
    }
  }

  if (!response.ok) {
    throw await parseError(response)
  }

  if (response.status === 204) {
    return undefined as T
  }
  return (await response.json()) as T
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),

  post: <T>(path: string, body?: unknown, options?: Partial<RequestOptions>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),

  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),

  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
